import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, isToday, isTomorrow, addDays, subDays, isSameDay } from 'date-fns';

const { FiTruck, FiPackage, FiMapPin, FiPhone, FiFileText, FiUser, FiExternalLink, FiCheckCircle, FiTrash2, FiCalendar, FiChevronLeft, FiChevronRight } = FiIcons;

const DeliveryManager = ({ orders, varieties, batches, customers, onUpdate }) => {
    const today = new Date();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [viewMode, setViewMode] = useState('day'); // 'day' | 'all'

    // 1. Calculate production tasks
    const productionTasks = orders.flatMap(order => {
        // Handling Sudden Orders
        if (order.isSudden) {
            return order.items.map((item, itemIdx) => {
                const variety = varieties.find(v => v.id === item.varietyId);
                if (!variety) return null;

                const deliveryDate = new Date(order.baseDate);
                const taskId = `${order.id}-${item.varietyId}-${itemIdx}`;
                const batch = batches.find(b => b.taskId === taskId);

                return {
                    taskId,
                    orderId: order.id,
                    customer: order.customer,
                    variety,
                    quantity: item.quantity,
                    bufferQuantity: 0,
                    sowingDate: null,
                    harvestDate: deliveryDate,
                    deliveryDate: deliveryDate,
                    batch,
                    isSudden: true
                };
            }).filter(Boolean);
        }

        // Standard Orders
        return order.items.map((item, itemIdx) => {
            const variety = varieties.find(v => v.id === item.varietyId);
            if (!variety) return null;

            const base = new Date(order.baseDate);
            const totalCycle = (variety.growingDays || 0) + (variety.darkTreatmentDays || 0);
            let sowingDate, deliveryDate;

            if (order.dateType === 'delivery') {
                deliveryDate = base;
                sowingDate = subDays(base, totalCycle);
            } else {
                sowingDate = base;
                deliveryDate = addDays(base, totalCycle);
            }

            const harvestDate = deliveryDate;
            const taskId = `${order.id}-${item.varietyId}-${itemIdx}`;
            const batch = batches.find(b => b.taskId === taskId);

            return {
                taskId,
                orderId: order.id,
                customer: order.customer,
                variety,
                quantity: item.quantity,
                bufferQuantity: item.bufferQuantity || 0,
                sowingDate,
                harvestDate,
                deliveryDate,
                batch,
                isSudden: false
            };
        }).filter(Boolean);
    });

    // 2. Filter Delivery Tasks
    const deliveryTasks = productionTasks.filter(task => {
        // Only show pending deliveries (not yet delivered)
        const isDelivered = task.batch?.delivered;
        if (isDelivered) return false;

        if (viewMode === 'all') {
            // Show all pending deliveries regardless of date
            return true;
        } else {
            // Show pending tasks for the selected date
            return isSameDay(task.deliveryDate, selectedDate);
        }
    });

    // 3. Group Tasks by Order
    const groupedOrders = deliveryTasks.reduce((acc, task) => {
        if (!acc[task.orderId]) {
            acc[task.orderId] = {
                orderId: task.orderId,
                customerName: task.customer,
                deliveryDate: task.deliveryDate,
                items: []
            };
        }
        acc[task.orderId].items.push(task);
        return acc;
    }, {});

    const sortedOrders = Object.values(groupedOrders).sort((a, b) => a.deliveryDate - b.deliveryDate);

    const handleDeleteOrder = async (orderId) => {
        if (!window.confirm(`Are you sure you want to delete Order #${orderId}? This will remove all associated tasks.`)) return;
        try {
            const response = await fetch(`/api/orders/${orderId}`, { method: 'DELETE' });
            if (response.ok) {
                if (onUpdate) onUpdate();
            }
        } catch (error) {
            console.error('Failed to delete order:', error);
        }
    };

    const markOrderComplete = async (orderItems) => {
        // Optimistically update or just wait for all
        await Promise.all(orderItems.map(task => markTaskComplete(task.taskId, true)));
        if (onUpdate) onUpdate();
    };

    const markTaskComplete = async (taskId, silent = false) => {
        const existingBatch = batches.find(b => b.taskId === taskId) || {};
        const newBatch = { ...existingBatch, taskId };
        newBatch.delivered = true;

        // Auto-fill missing steps if skipping straight to delivery
        if (!newBatch.actualHarvestDate) newBatch.actualHarvestDate = format(today, 'yyyy-MM-dd');
        if (!newBatch.actualSowingDate) newBatch.actualSowingDate = format(today, 'yyyy-MM-dd');

        try {
            const response = await fetch('/api/batches', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newBatch)
            });

            if (response.ok) {
                if (onUpdate && !silent) onUpdate();
            }
        } catch (error) {
            console.error('Failed to update task:', error);
        }
    };

    return (
        <div className="p-6 md:p-10 max-w-7xl mx-auto pb-24 md:pb-10">
            <div className="mb-8 md:mb-12 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] tracking-tight">Delivery Management</h1>

                    <div className="flex items-center space-x-4 mt-4">
                        <div className="bg-gray-100 p-1 rounded-xl flex items-center">
                            <button onClick={() => setViewMode('day')} className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${viewMode === 'day' ? 'bg-white text-[#1e3a8a] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>Daily</button>
                            <button onClick={() => setViewMode('all')} className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${viewMode === 'all' ? 'bg-white text-[#1e3a8a] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>All Pending</button>
                        </div>

                        {viewMode === 'day' && (
                            <div className="flex items-center space-x-2 bg-white p-1 rounded-xl border border-gray-100 shadow-sm w-fit">
                                <button onClick={() => setSelectedDate(subDays(selectedDate, 1))} className="p-2 hover:bg-gray-50 rounded-lg text-gray-500 transition-colors">
                                    <SafeIcon icon={FiChevronLeft} />
                                </button>
                                <div className="flex items-center space-x-2 px-2 text-gray-600 font-medium min-w-[140px] justify-center">
                                    <SafeIcon icon={FiCalendar} />
                                    <span className="uppercase tracking-widest text-[11px]">{format(selectedDate, 'EEE, MMM dd, yyyy')}</span>
                                </div>
                                <button onClick={() => setSelectedDate(addDays(selectedDate, 1))} className="p-2 hover:bg-gray-50 rounded-lg text-gray-500 transition-colors">
                                    <SafeIcon icon={FiChevronRight} />
                                </button>
                                {!isSameDay(selectedDate, today) && (
                                    <button onClick={() => setSelectedDate(today)} className="text-[10px] font-bold uppercase tracking-widest text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors ml-1">
                                        Today
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-8">
                {/* Section Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 rounded-xl bg-[#1e3a8a] flex items-center justify-center shadow-md shadow-blue-50">
                            <SafeIcon icon={FiTruck} className="text-white text-lg" />
                        </div>
                        <h3 className="text-xl font-light text-[#1e3a8a]">Orders to Ship</h3>
                    </div>
                    <span className="bg-blue-50 text-blue-700 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">{sortedOrders.length} ORDERS</span>
                </div>

                {sortedOrders.length === 0 ? (
                    <div className="text-center py-20 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-gray-300">
                            <SafeIcon icon={FiTruck} className="text-2xl" />
                        </div>
                        <p className="text-gray-400 text-sm font-medium uppercase tracking-widest">No orders pending delivery on this date</p>
                    </div>
                ) : (
                    sortedOrders.map(order => {
                        const customerDetails = customers ? customers.find(c => c.name === order.customerName) : null;

                        return (
                            <div key={order.orderId} className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden hover:border-blue-200 transition-all">
                                {/* Order Header */}
                                <div className="bg-white p-6 border-b border-gray-200 flex flex-col md:flex-row justify-between md:items-start gap-6">
                                    <div className="flex items-start space-x-4">
                                        <div className="w-16 h-16 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-300 shadow-sm overflow-hidden flex-shrink-0">
                                            {customerDetails && customerDetails.image ? (
                                                <img src={customerDetails.image} alt={order.customerName} className="w-full h-full object-cover" />
                                            ) : (
                                                <SafeIcon icon={FiUser} className="text-2xl" />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-[#1e3a8a]">{order.customerName}</h3>
                                            <div className="flex flex-col space-y-1 mt-2 text-sm text-gray-500">
                                                {customerDetails && customerDetails.address && (
                                                    <div className="flex items-start space-x-2">
                                                        <SafeIcon icon={FiMapPin} className="mt-0.5 text-gray-400 flex-shrink-0" />
                                                        <span className="max-w-md leading-tight">{customerDetails.address} {customerDetails.city} {customerDetails.zipCode}</span>
                                                    </div>
                                                )}
                                                {customerDetails && customerDetails.contact && (
                                                    <div className="flex items-center space-x-2">
                                                        <SafeIcon icon={FiPhone} className="text-gray-400 flex-shrink-0" />
                                                        <span>{customerDetails.contact}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col items-end space-y-3">
                                        <div className="flex items-center space-x-3">
                                            <div className="text-right mr-2">
                                                <span className="block text-[10px] font-bold uppercase tracking-widest text-gray-400">Order ID</span>
                                                <span className="text-lg font-light text-gray-900">#{order.orderId}</span>
                                            </div>
                                            <button
                                                onClick={() => markOrderComplete(order.items)}
                                                className="bg-[#1e3a8a] text-white hover:bg-blue-800 px-6 py-2.5 rounded-xl text-sm font-bold shadow-md shadow-blue-100 transition-all flex items-center space-x-2"
                                            >
                                                <SafeIcon icon={FiCheckCircle} />
                                                <span>Mark Delivered</span>
                                            </button>
                                            <button
                                                onClick={() => handleDeleteOrder(order.orderId)}
                                                className="bg-red-50 text-red-600 hover:bg-red-100 p-2.5 rounded-xl transition-all shadow-sm"
                                                title="Delete Order"
                                            >
                                                <SafeIcon icon={FiTrash2} />
                                            </button>
                                        </div>

                                        {customerDetails && customerDetails.locationLink && (
                                            <a href={customerDetails.locationLink} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg transition-colors">
                                                <SafeIcon icon={FiMapPin} />
                                                <span>Map Location</span>
                                                <SafeIcon icon={FiExternalLink} className="text-[10px]" />
                                            </a>
                                        )}
                                    </div>
                                </div>

                                {/* SOP Section */}
                                {customerDetails && customerDetails.deliverySOP && (
                                    <div className="px-6 py-3 bg-yellow-50 border-b border-yellow-100 flex items-start space-x-3 text-sm text-yellow-800">
                                        <SafeIcon icon={FiFileText} className="mt-0.5 flex-shrink-0 text-yellow-600" />
                                        <p className="font-medium leading-relaxed">{customerDetails.deliverySOP}</p>
                                    </div>
                                )}

                                {/* Order Items */}
                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {order.items.map(task => (
                                            <div key={task.taskId} className="flex flex-col p-4 rounded-xl border border-gray-200 bg-white hover:border-blue-300 transition-all shadow-sm">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div>
                                                        <span className="text-lg font-bold text-gray-800 block">{task.variety.name}</span>
                                                        {task.isSudden && (
                                                            <span className="inline-block mt-1 text-[10px] font-bold text-white bg-orange-400 px-2 py-0.5 rounded-full">STOCK ORDER</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="mt-auto flex items-center space-x-2">
                                                    <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-bold rounded-lg border border-green-100">
                                                        Qty: {task.quantity}
                                                    </span>
                                                    {task.bufferQuantity > 0 && (
                                                        <span className="px-3 py-1 bg-orange-50 text-orange-700 text-xs font-bold rounded-lg border border-orange-100">
                                                            Buffer: +{task.bufferQuantity}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default DeliveryManager;
